export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

export interface ApiRequestOptions {
  method: HttpMethod
  url: string
  headers?: Record<string, string>
  query?: Record<string, string>
  body?: unknown
  timeoutMs?: number
  maxRetries?: number
  authToken?: string | null
}

export interface ApiResponse<T = unknown> {
  ok: boolean
  status: number
  data: T | null
  headers: Record<string, string>
  duration: number
  attempts: number
}

export type MockHandler = (request: ApiRequestOptions) => Promise<{
  status: number
  data?: unknown
}> | {
  status: number
  data?: unknown
}

/**
 * Retry with exponential backoff: attempt 1, 2, 3...
 * delay = baseMs * 2^(attempt-1)
 */
export async function withRetry<T>(
  fn: (attempt: number) => Promise<T>,
  maxRetries = 3,
  baseDelayMs = 50,
): Promise<{ value: T; attempts: number }> {
  let lastError: unknown
  const attemptsLimit = Math.max(1, maxRetries)

  for (let attempt = 1; attempt <= attemptsLimit; attempt += 1) {
    try {
      const value = await fn(attempt)
      return { value, attempts: attempt }
    } catch (error) {
      lastError = error
      if (attempt >= attemptsLimit) break
      const delay = baseDelayMs * 2 ** (attempt - 1)
      await sleep(delay)
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Retry esgotado')
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * ApiClient — HTTP abstraction with timeout, retry, headers and auth.
 * Sprint 1.2 uses a mock transport (no real network).
 */
export class ApiClient {
  private mockHandler: MockHandler = async () => ({ status: 200, data: { ok: true } })
  private readonly defaults: { timeoutMs: number; maxRetries: number }

  constructor(
    defaults: {
      timeoutMs: number
      maxRetries: number
    } = { timeoutMs: 8000, maxRetries: 3 },
  ) {
    this.defaults = defaults
  }

  setMockHandler(handler: MockHandler): void {
    this.mockHandler = handler
  }

  get<T = unknown>(url: string, options: Omit<ApiRequestOptions, 'method' | 'url'> = {}) {
    return this.request<T>({ ...options, method: 'GET', url })
  }

  post<T = unknown>(url: string, body?: unknown, options: Omit<ApiRequestOptions, 'method' | 'url' | 'body'> = {}) {
    return this.request<T>({ ...options, method: 'POST', url, body })
  }

  put<T = unknown>(url: string, body?: unknown, options: Omit<ApiRequestOptions, 'method' | 'url' | 'body'> = {}) {
    return this.request<T>({ ...options, method: 'PUT', url, body })
  }

  delete<T = unknown>(url: string, options: Omit<ApiRequestOptions, 'method' | 'url'> = {}) {
    return this.request<T>({ ...options, method: 'DELETE', url })
  }

  async request<T = unknown>(options: ApiRequestOptions): Promise<ApiResponse<T>> {
    const timeoutMs = options.timeoutMs ?? this.defaults.timeoutMs
    const maxRetries = options.maxRetries ?? this.defaults.maxRetries
    const started = performance.now()

    const { value, attempts } = await withRetry(async () => {
      return this.executeOnce<T>(options, timeoutMs)
    }, maxRetries)

    return {
      ...value,
      duration: Math.round(performance.now() - started),
      attempts,
    }
  }

  private async executeOnce<T>(
    options: ApiRequestOptions,
    timeoutMs: number,
  ): Promise<Omit<ApiResponse<T>, 'duration' | 'attempts'>> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    }
    if (options.authToken) headers.Authorization = `Bearer ${options.authToken}`

    const result = await Promise.race([
      Promise.resolve(this.mockHandler({ ...options, headers })),
      sleepReject(timeoutMs, `Timeout após ${timeoutMs}ms`),
    ])

    if (result.status >= 500) {
      throw new Error(`HTTP ${result.status}`)
    }

    return {
      ok: result.status >= 200 && result.status < 300,
      status: result.status,
      data: (result.data as T) ?? null,
      headers,
    }
  }
}

function sleepReject(ms: number, message: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms)
  })
}

export const apiClient = new ApiClient()
